import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import NavBar from './components/NavBar';
import ArticleStatsPage from './pages/ArticleStatsPage';
import ArticlesPage from './pages/ArticlesPage';
import CacheStatusPage from './pages/CacheStatusPage';
import CustomerOrderMatrixPage from './pages/CustomerOrderMatrixPage';
import CustomerSalesTrendsPage from './pages/CustomerSalesTrendsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import CrmDashboardPage from './pages/CrmDashboardPage';
import CrmStatsPage from './pages/CrmStatsPage';
import HomePage from './pages/HomePage';
import InventoryPage from './pages/InventoryPage';
import InvoicesPage from './pages/InvoicesPage';
import LoginPage, { LandingPage } from './pages/LoginPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import RequestInfoPage from './pages/RequestInfoPage';
import SalesTrendsPage from './pages/SalesTrendsPage';
import StatisticsPage from './pages/StatisticsPage';
import SupplierInvoicesPage from './pages/SupplierInvoicesPage';
import TenantSettingsPage from './pages/TenantSettingsPage';
import UsersPage from './pages/UsersPage';
import useAuthSession from './hooks/useAuthSession';

const supplierInvoicesEnabled = process.env.REACT_APP_SUPPLIER_INVOICES_ENABLED === 'true';
const ADMIN_ROLES = new Set(['TENANT_ADMIN', 'SUPER_ADMIN']);
const PUBLIC_MARKETING_PATHS = new Set(['/login', '/request-info']);
const TENANT_SCOPED_PATHS = new Set([
  '/orders',
  '/crm',
  '/crm/stats',
  '/customers',
  '/invoices',
  '/articles',
  '/article-stats',
  '/purchase-orders',
  '/supplier-invoices',
  '/sales-trends',
  '/customer-sales-trends',
  '/customer-order-matrix',
  '/statistics',
  '/inventory',
  '/cache-status',
]);

function App() {
  return (
    <Router>
      <Root />
    </Router>
  );
}

function Root() {
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    actingTenant,
    authResolved,
    clearActingTenant,
    connected,
    crmEnabled,
    currentUser,
    loadingConnection,
    loggedIn,
    login,
    logout,
    refreshConnection,
    restoreSession,
    setActingTenant,
    setConnected,
    tenantContextRequired,
  } = useAuthSession();

  useEffect(() => {
    if (!authResolved || !loggedIn || tenantContextRequired) {
      return;
    }

    refreshConnection().catch((connectionError) => {
      console.error('Error checking connection status:', connectionError);
      setError(connectionError.message || 'Failed to refresh the Fortnox connection state.');
    });
  }, [authResolved, loggedIn, tenantContextRequired, refreshConnection]);

  const isTenantScopedPath = (pathname) =>
    TENANT_SCOPED_PATHS.has(pathname) || pathname.startsWith('/customers/');

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleClearActingTenant = async () => {
    try {
      await clearActingTenant();

      if (isTenantScopedPath(location.pathname)) {
        navigate('/', { replace: true });
      }
    } catch (clearError) {
      console.error('Failed to clear tenant context:', clearError);
      setError(clearError.message || 'Failed to clear the tenant context.');
      throw clearError;
    }
  };

  const handleSetActingTenant = async (tenantId) => {
    try {
      return await setActingTenant(tenantId);
    } catch (tenantError) {
      console.error('Failed to set tenant context:', tenantError);
      setError(tenantError.message || 'Failed to switch tenant context.');
      throw tenantError;
    }
  };

  const handleRefreshSession = async () => {
    try {
      return await restoreSession();
    } catch (sessionError) {
      console.error('Failed to refresh the auth session:', sessionError);
      setError(sessionError.message || 'Failed to refresh the account session.');
      throw sessionError;
    }
  };

  const shouldShowNavBar =
    loggedIn && location.pathname !== '/' && !PUBLIC_MARKETING_PATHS.has(location.pathname);
  const isSalesTrendsPath = location.pathname === '/sales-trends';
  const isCustomerSalesPath = location.pathname === '/customer-sales-trends';

  const renderProtectedRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    return loggedIn ? element : <Navigate to="/login" replace />;
  };

  const renderTenantScopedRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (tenantContextRequired) {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  const renderAdminRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (!ADMIN_ROLES.has(currentUser?.role)) {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  const renderSuperAdminRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (currentUser?.role !== 'SUPER_ADMIN') {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  const renderCrmRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (tenantContextRequired || !crmEnabled) {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  const renderCrmAdminRoute = (element) => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (!ADMIN_ROLES.has(currentUser?.role) || tenantContextRequired || !crmEnabled) {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  const persistentCustomerSalesPage = useMemo(
    () => (
      <CustomerSalesTrendsPage
        connected={connected}
        setConnected={setConnected}
        setError={setError}
      />
    ),
    [connected, setConnected, setError]
  );

  const persistentSalesTrendsPage = useMemo(
    () => (
      <SalesTrendsPage
        connected={connected}
        setConnected={setConnected}
        setError={setError}
      />
    ),
    [connected, setConnected, setError]
  );

  const renderPersistentSalesTrendsPage = () => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return isSalesTrendsPath ? <Navigate to="/login" replace /> : null;
    }

    if (tenantContextRequired) {
      return isSalesTrendsPath ? <Navigate to="/" replace /> : null;
    }

    return (
      <div style={{ display: isSalesTrendsPath ? 'block' : 'none' }}>
        {persistentSalesTrendsPage}
      </div>
    );
  };

  const renderPersistentCustomerSalesPage = () => {
    if (!authResolved) {
      return null;
    }

    if (!loggedIn) {
      return isCustomerSalesPath ? <Navigate to="/login" replace /> : null;
    }

    if (tenantContextRequired) {
      return isCustomerSalesPath ? <Navigate to="/" replace /> : null;
    }

    return (
      <div style={{ display: isCustomerSalesPath ? 'block' : 'none' }}>
        {persistentCustomerSalesPage}
      </div>
    );
  };

  return (
    <>
      {shouldShowNavBar && (
        <NavBar
          crmEnabled={crmEnabled}
          currentUser={currentUser}
          onLogout={handleLogout}
          supplierInvoicesEnabled={supplierInvoicesEnabled}
          tenantContextRequired={tenantContextRequired}
        />
      )}

      <Box sx={{ p: { xs: 0, md: 2 } }}>
        {error && (
          <div
            style={{
              position: 'fixed',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'red',
              color: 'white',
              padding: '10px',
              zIndex: 1000,
            }}
          >
            {error}
            <button
              onClick={() => setError(null)}
              style={{
                marginLeft: '10px',
                color: 'white',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        <Routes>
          <Route
            path="/login"
            element={
              !authResolved ? null : loggedIn ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={login} />
              )
            }
          />

          <Route path="/request-info" element={<RequestInfoPage />} />

          <Route
            path="/"
            element={
              !authResolved ? null : loggedIn ? (
                <HomePage
                  actingTenant={actingTenant}
                  connected={connected}
                  crmEnabled={crmEnabled}
                  currentUser={currentUser}
                  onClearActingTenant={handleClearActingTenant}
                  onLogout={handleLogout}
                  onSetActingTenant={handleSetActingTenant}
                  setConnected={setConnected}
                  supplierInvoicesEnabled={supplierInvoicesEnabled}
                  tenantContextRequired={tenantContextRequired}
                />
              ) : (
                <LandingPage />
              )
            }
          />

          <Route
            path="/users"
            element={renderAdminRoute(<UsersPage currentUser={currentUser} />)}
          />

          <Route
            path="/tenant-settings"
            element={renderSuperAdminRoute(
              <TenantSettingsPage
                actingTenant={actingTenant}
                onRefreshSession={handleRefreshSession}
                setError={setError}
              />
            )}
          />

          <Route
            path="/profile"
            element={renderProtectedRoute(
              <ProfilePage currentUser={currentUser} onRefreshSession={handleRefreshSession} />
            )}
          />

          <Route
            path="/orders"
            element={renderTenantScopedRoute(
              <OrdersPage connected={connected} setConnected={setConnected} setError={setError} />
            )}
          />
          <Route
            path="/crm"
            element={renderCrmRoute(
              <CrmDashboardPage
                connected={connected}
                currentUser={currentUser}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/crm/stats"
            element={renderCrmAdminRoute(
              <CrmStatsPage
                connected={connected}
                currentUser={currentUser}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/customers"
            element={renderCrmRoute(
              <CustomersPage connected={connected} setConnected={setConnected} setError={setError} />
            )}
          />
          <Route
            path="/customers/:customerNumber"
            element={renderCrmRoute(
              <CustomerDetailPage
                connected={connected}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/invoices"
            element={renderTenantScopedRoute(
              <InvoicesPage connected={connected} setConnected={setConnected} setError={setError} />
            )}
          />
          <Route
            path="/customer-order-matrix"
            element={renderTenantScopedRoute(
              <CustomerOrderMatrixPage
                connected={connected}
                loadingConnection={loadingConnection}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/article-stats"
            element={renderTenantScopedRoute(
              <ArticleStatsPage connected={connected} setConnected={setConnected} setError={setError} />
            )}
          />
          <Route
            path="/inventory"
            element={renderTenantScopedRoute(
              <InventoryPage connected={connected} setConnected={setConnected} setError={setError} />
            )}
          />
          <Route
            path="/cache-status"
            element={renderTenantScopedRoute(
              <CacheStatusPage
                actingTenant={actingTenant}
                connected={connected}
                currentUser={currentUser}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/statistics"
            element={renderTenantScopedRoute(
              <StatisticsPage
                connected={connected}
                loadingConnection={loadingConnection}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/articles"
            element={renderTenantScopedRoute(
              <ArticlesPage
                connected={connected}
                loadingConnection={loadingConnection}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/purchase-orders"
            element={renderTenantScopedRoute(
              <PurchaseOrdersPage
                connected={connected}
                loadingConnection={loadingConnection}
                setConnected={setConnected}
                setError={setError}
              />
            )}
          />
          <Route
            path="/supplier-invoices"
            element={
              !authResolved ? null : !loggedIn ? (
                <Navigate to="/login" replace />
              ) : !supplierInvoicesEnabled ? (
                <Navigate to="/" replace />
              ) : tenantContextRequired ? (
                <Navigate to="/" replace />
              ) : (
                <SupplierInvoicesPage
                  connected={connected}
                  loadingConnection={loadingConnection}
                  setConnected={setConnected}
                  setError={setError}
                />
              )
            }
          />
        </Routes>

        {renderPersistentSalesTrendsPage()}
        {renderPersistentCustomerSalesPage()}
      </Box>
    </>
  );
}

export default App;
